import { HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { StringHelper } from '../../shared/string-helper';
import { TokenService } from './token.service';

// A request body here is a genuine HTTP boundary: this shared helper forwards whatever DTO a caller
// passes straight to HttpClient, so the concrete shape is only known at the call site. Bodies are typed
// as the generic `TBody` (inferred from the argument) and responses as `TResponse` (which a caller can
// specify, e.g. `getWithAccessToken<VmStaffInfo>(url)`). `TResponse` defaults to `NonNullable<unknown>` —
// the lint-clean equivalent of HttpClient's own `Object` default, "any non-nullish value" — so every
// existing caller keeps the exact type it had before.
@Injectable({
  providedIn: 'root',
})
export class ApiHelper {
  constructor(
    private readonly http: HttpClient,
    public stringHelper: StringHelper,
    private readonly tokenService: TokenService
  ) {}

  getOptionsWithAccessToken(): { headers: HttpHeaders } {
    const token = this.tokenService.getAccessToken();
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + token);
    const options = { headers: headers };
    return options;
  }

  getWithAccessToken<TResponse = NonNullable<unknown>>(
    url: string,
    params?: HttpParams,
    context?: HttpContext
  ): Observable<TResponse> {
    const options = this.getOptionsWithAccessToken();
    return this.http.get<TResponse>(url, { ...options, params, context });
  }

  getFileWithAccessToken(url: string): Observable<Blob> {
    const options = this.getOptionsWithAccessToken();
    return this.http.get(url, { responseType: 'blob', headers: options.headers });
  }

  putWithAccessToken<TBody, TResponse = NonNullable<unknown>>(url: string, payload: TBody): Observable<TResponse> {
    const options = this.getOptionsWithAccessToken();
    return this.http.put<TResponse>(url, payload, options);
  }

  postWithAccessToken<TBody, TResponse = NonNullable<unknown>>(url: string, payload: TBody): Observable<TResponse> {
    const options = this.getOptionsWithAccessToken();
    return this.http.post<TResponse>(url, payload, options);
  }

  deleteWithAccessToken<TResponse = NonNullable<unknown>>(url: string, params?: HttpParams): Observable<TResponse> {
    const options = this.getOptionsWithAccessToken();
    return this.http.delete<TResponse>(url, { ...options, params });
  }

  postFileWithAccessToken<TBody, TResponse = NonNullable<unknown>>(url: string, payload: TBody): Observable<TResponse> {
    const token = this.tokenService.getAccessToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    const options = { headers: headers };
    return this.http.post<TResponse>(url, payload, { ...options });
  }

  patchWithAccessToken<TBody, TResponse = NonNullable<unknown>>(url: string, payload: TBody): Observable<TResponse> {
    const options = this.getOptionsWithAccessToken();
    return this.http.patch<TResponse>(url, payload, options);
  }

  postWithAccessTokenObserveResponse<TBody, TResponse = NonNullable<unknown>>(
    url: string,
    payload: TBody
  ): Observable<HttpResponse<TResponse>> {
    const options = this.getOptionsWithAccessToken();
    return this.http.post<TResponse>(url, payload, { ...options, observe: 'response' });
  }
}
